import * as React from 'react'
import withHydrationOnDemand from 'react-hydration-on-demand'
import dynamic from 'next/dynamic'

import { ContentItem, PricePerUnit } from '@thg-commerce/enterprise-components'
import { PowerReviewCategorySnippet } from '@thg-commerce/enterprise-components/PowerReviewCategorySnippet'
import { getPowerReviewGroups } from '@thg-commerce/enterprise-components/src/PowerReview/PowerReview'
import { VariantFields } from '@thg-commerce/enterprise-components/src/transformer/productOptionsTransformer'
import {
  i18n,
  useFormattableI18nProperty,
  useSiteConfig,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import {
  ManualRecommendationOptions,
  ProductPageData,
  SelectedOptions,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product/ProductPage'
import { ProductVariantWithDescription } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/Product'
import {
  ChoiceMap,
  VariantChoices,
} from '@thg-commerce/enterprise-network/src/transformers/product/options'
import { useTheme } from '@thg-commerce/enterprise-pages/src/Product/theme'
import { TransformedOption } from '@thg-commerce/enterprise-product-options/src/transformers/option'
import { getPrice } from '@thg-commerce/enterprise-product-options/src/utils/price'
import {
  BrandLogo,
  Price,
  ProductImage,
  ProductReview,
} from '@thg-commerce/gravity-elements'
import { SwatchShape } from '@thg-commerce/gravity-elements/Swatch/types'
import { MarketedSpecialOfferSummaryContainer } from '@thg-commerce/gravity-patterns'
import { MarketedSpecialOfferSummaryContainerProps } from '@thg-commerce/gravity-patterns/MarketedSpecialOfferSummary'
import { spacing } from '@thg-commerce/gravity-theme'

import { PowerReviewReviewSnippet } from '../../../components/PowerReviewReviewSnippet'
import { SizeGuideContainerProps } from '../../../components/SizeGuideModal/SizeGuideContainer/SizeGuideContainer'
import { OutOfStockNotificationRenderer } from '../../OutOfStockNotificationRenderer'
import { ProductOptionSelectorRenderer as ProductOptionsSelectorRendererComponent } from '../../ProductOptionSelectorRenderer'

import {
  AttributeTitle,
  MobileOptionButtonContainer,
  OptionsButtonContainer,
  RecommendationItemContainer,
  StyledButton,
  Title,
} from './style'

const SizeGuideContainer = dynamic<SizeGuideContainerProps>(() =>
  import('../../../components/SizeGuideModal/SizeGuideContainer').then(
    (mod) => mod.SizeGuideContainer,
  ),
)

const ProductOptionSelectorRenderer = withHydrationOnDemand({
  on: [['idle']],
})(ProductOptionsSelectorRendererComponent)

const SizeGuideContainerComponent = withHydrationOnDemand({
  on: ['visible'],
})(SizeGuideContainer)

const PowerReviewCategorySnippetRenderer = withHydrationOnDemand({
  on: [['idle']],
})(PowerReviewCategorySnippet)

interface RecommendationItemProps {
  product: ManualRecommendationOptions | ProductPageData
  selectedVariant: ProductVariantWithDescription
  content?: ContentItem[]
  /** Variant changed callback is only passed through for the master sku. The state of this is handled in ProductContext */
  variantChangedCallback?: (product: any) => void
  selectedOptions?: SelectedOptions
  /** Options changed callback is only passed through for the master sku. The state of this is handled in ProductContext */
  optionsChangedCallback?: (options: SelectedOptions) => void
  button?: {
    handleClick: (sku: number, quantity?: number) => void
    title: string
  }
  options?: VariantChoices | TransformedOption[]
  url?: string
  mainProduct?: boolean
  choiceMap?: ChoiceMap
  showCheckStoreMessage?: boolean | false
  isFirstRecommendation?: boolean
  marketedSpecialOffer?: MarketedSpecialOfferSummaryContainerProps | null
  storeStockActive?: boolean
  setStoreStockActive?: (value: boolean) => void
}

const getEnableSavePrice = (
  isFirstRecommendationItem: boolean,
  disableSavingOnRecommendations: boolean,
  enableSavePrice: boolean,
  themeEnableSavePrice: boolean,
) => {
  if (disableSavingOnRecommendations && !isFirstRecommendationItem) {
    return false
  }
  return enableSavePrice || themeEnableSavePrice || false
}

export const RecommendationItem = (props: RecommendationItemProps) => {
  const theme = useTheme()
  const { defaultLocale, defaultCurrency } = useSiteDefinition()
  const {
    choiceMap,
    isFirstRecommendation,
    options,
    optionsChangedCallback,
    product,
    variantChangedCallback: variantChangedCallbackProp,
    storeStockActive,
    setStoreStockActive,
  } = props

  const {
    showPowerReview,
    enablePricePerUnit,
    papMessageTopPosition,
    enablePapOnManualRecs,
    enableQuantityOnManualRecs,
    showProductBlockBrandLogo,
    disableRecommendationFirstImageAndTitle,
    enableSavePrice,
    disableSavingOnRecommendations,
  } = useSiteConfig()
  const [selectedVariant, setSelectedVariant] = React.useState<
    ProductVariantWithDescription
  >(props.product.defaultVariant)

  const [selectedOptions, setSelectedOptions] = React.useState<SelectedOptions>(
    props.product.selectedOptions
      ? props.product.selectedOptions
      : props.product.defaultVariant.selectedOptions
      ? props.product.defaultVariant.selectedOptions
      : {},
  )
  const selectedVariantValue = props.variantChangedCallback
    ? props.selectedVariant
    : selectedVariant

  const selectedOptionsValue =
    props.optionsChangedCallback && props.selectedOptions
      ? props.selectedOptions
      : selectedOptions

  const i18nText = {
    addToBasketText: i18n('product.optionselector.addtobasket.text'),
    reviewsStarsLabel: i18n('reviews.stars.label'),
    reviewsLabel: i18n('reviews.reviews.label'),
    rrpLabel: i18n('general.rrp.text'),
    saveLabel: i18n('general.save.text'),
    limit: i18n('product.maxquantity.basket.limit.text'),
    singleItemText: i18n('product.maxquantity.basket.singleitem.text'),
    multipleItemsText: i18n('product.maxquantity.basket.multipleitems.text'),
    brandLogoLabel: useFormattableI18nProperty('product.brand.logo.label'),
    marketedSpecialOfferText: {
      closeI18nText: {
        closeAriaLabel: i18n('general.modal.close.button.arialabel'),
        closeLabel: i18n('general.modal.close.button.label'),
      },
      freeGiftMessage: i18n('product.marketedspecialoffer.freegift.text'),
    },
  }

  const variantChangedCallback = (variant: ProductPageData['variants'][0]) => {
    setSelectedVariant && setSelectedVariant(variant)
  }

  const setSelectedOptionsCallback = (selectedOptions: SelectedOptions) => {
    setSelectedOptions(selectedOptions)
  }

  const powerReviewGroupID = getPowerReviewGroups(props.content)

  const hideImageTitle = disableRecommendationFirstImageAndTitle
    ? !props.isFirstRecommendation
    : true

  const showBrandLogo =
    props.product?.brand?.imageUrl &&
    !props.isFirstRecommendation &&
    showProductBlockBrandLogo

  const brandLabel = i18nText.brandLogoLabel(props.product.brand?.name)

  const [activeTabIndex, setActiveTabIndex] = React.useState(0)

  // @TODO: REBUILD-6279: simplify to pass link through and separate out Size guide component
  const Options = React.useMemo(
    () => (
      <ProductOptionSelectorRenderer
        linkedOn={product.linkedOn}
        swatchShape={SwatchShape.CIRCLE}
        showSwatches={
          theme.pageTheme.manualRecommendation?.swatch?.show ?? true
        }
        variantChangedCallback={
          variantChangedCallbackProp
            ? variantChangedCallbackProp
            : variantChangedCallback
        }
        product={product}
        labelCustomComponent={
          <SizeGuideContainerComponent
            sizeGuide={product.sizeGuide}
            modalLinkTextStyling={
              theme.pageTheme.sizeGuide.modalLink?.textStyle
            }
          />
        }
        setSelectedOptionsCallback={
          optionsChangedCallback
            ? optionsChangedCallback
            : setSelectedOptionsCallback
        }
        selectedOptions={selectedOptionsValue}
        choiceMap={choiceMap ? choiceMap : product.choiceMap}
        options={options || []}
        selectedVariant={selectedVariantValue}
        defaultVariant={product.defaultVariant}
        variants={product?.variants || []}
        isFirstProductRecommendation={isFirstRecommendation}
        notifyWhenInStock={product.hasVariantsWithNotifyWhenInStockEnabled}
        enablePap={
          isFirstRecommendation ? Boolean(enablePapOnManualRecs) : false
        }
        enableQuantity={
          isFirstRecommendation ? Boolean(enableQuantityOnManualRecs) : false
        }
        masterProductSku={product.sku}
        initialSelectedOptions={product.selectedOptions}
        sortedOptionKeys={product.sortedOptionKeys}
        defaultProductOptions={product.defaultProductOptions}
        queryFullVariantData={isFirstRecommendation}
        activeTabIndex={activeTabIndex}
        setActiveTabIndex={setActiveTabIndex}
        storeStockActive={storeStockActive}
        setStoreStockActive={setStoreStockActive}
      />
    ),
    [
      selectedOptionsValue,
      selectedVariantValue,
      enablePapOnManualRecs,
      enableQuantityOnManualRecs,
      choiceMap,
      isFirstRecommendation,
      options,
      optionsChangedCallback,
      product,
      variantChangedCallbackProp,
      theme.pageTheme.manualRecommendation?.swatch?.show,
      theme.pageTheme.sizeGuide.modalLink?.textStyle,
      storeStockActive,
      setStoreStockActive,
    ],
  )

  const productTitle = (selectedVariantValue || props.product).title

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(1),
      }}
      data-testid="recommendation-item"
    >
      <RecommendationItemContainer>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {showBrandLogo && (
            <BrandLogo
              brandLabel={brandLabel}
              imageUrl={props.product.brand?.imageUrl}
            />
          )}
          {hideImageTitle && (
            <ProductImage
              urls={{
                largeProduct:
                  selectedVariantValue?.images[0]?.largeProduct ||
                  props.product.images[0]?.largeProduct ||
                  '',
              }}
              alt={(selectedVariantValue || props.product).title}
              isAmp={false}
              width={theme.patterns.manualRecommendations.image.width}
              aspectRatio={theme.patterns.imageGallery.image.aspectRatio}
            />
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%',
            gap: spacing(1),
          }}
        >
          {hideImageTitle && (
            <div>
              {props.url ? (
                <StyledButton
                  sizing="regular"
                  emphasis="low"
                  renderedAs="a"
                  href={props.url}
                >
                  <Title data-testid="recommendation-item-title">
                    {productTitle}
                  </Title>
                </StyledButton>
              ) : (
                <Title>{productTitle}</Title>
              )}
            </div>
          )}
          {showPowerReview && !props.isFirstRecommendation && (
            <PowerReviewCategorySnippetRenderer
              externalId={Number(selectedVariantValue?.externalIdentifier)}
              sku={Number(selectedVariantValue?.sku)}
              locale={defaultLocale}
              id={props.product.sku}
              powerReviewGroups={powerReviewGroupID}
            />
          )}
          {showPowerReview && props.isFirstRecommendation && (
            <PowerReviewReviewSnippet
              sku={Number(selectedVariantValue?.sku)}
              externalId={Number(selectedVariantValue?.externalIdentifier)}
              locale={defaultLocale}
              pathname={String(selectedVariantValue?.url)}
              powerReviewGroups={powerReviewGroupID}
            />
          )}
          <Price
            priceTheme={{
              price: theme.pageTheme.productSummary.price.price,
              rrp: theme.pageTheme.productSummary.price.rrp,
              savings: theme.pageTheme.productSummary.price.savings,
              priceWithRRP: theme.pageTheme.productSummary.price.priceWithRRP,
            }}
            priceDirection={
              theme.pageTheme.manualRecommendation?.price?.direction
            }
            displayRRPText={theme.pageTheme.productSummary.price.displayRRPText}
            pricing={getPrice(
              selectedVariantValue,
              defaultCurrency,
              defaultLocale,
            )}
            savings={{
              show: getEnableSavePrice(
                isFirstRecommendation,
                disableSavingOnRecommendations,
                enableSavePrice,
                theme.pageTheme.productSummary.price.savings.show,
              ),
            }}
            i18nText={{
              rrpLabel: i18nText.rrpLabel,
              saveLabel: theme.pageTheme.productSummary.price.savings.label.show
                ? i18nText.saveLabel
                : undefined,
            }}
          />
          {enablePricePerUnit && (
            <PricePerUnit pricePerUnit={props.product.pricePerUnit} />
          )}
          {props.variantChangedCallback &&
            papMessageTopPosition &&
            enablePapOnManualRecs &&
            props.product?.marketedSpecialOffer && (
              <MarketedSpecialOfferSummaryContainer
                {...props.product?.marketedSpecialOffer}
                i18nText={i18nText.marketedSpecialOfferText}
                onlyDisplayOfferBadge={papMessageTopPosition}
                reduceBottomMarginContainer={papMessageTopPosition}
                badgeStyleOverride={theme.pageTheme.marketedSpecialOffer.badge}
                landingPageLink={
                  props.product?.marketedSpecialOffer?.landingPageLink ||
                  undefined
                }
                containerStyleOverride={
                  theme.pageTheme.marketedSpecialOffer.containerStyle
                }
                modalStyleOverride={theme.pageTheme.marketedSpecialOffer.modal}
              />
            )}
          {!props.variantChangedCallback &&
            papMessageTopPosition &&
            enablePapOnManualRecs &&
            props.product?.marketedSpecialOffer && (
              <MarketedSpecialOfferSummaryContainer
                {...props.product?.marketedSpecialOffer}
                i18nText={i18nText.marketedSpecialOfferText}
                onlyDisplayOfferBadge={papMessageTopPosition}
                reduceBottomMarginContainer={papMessageTopPosition}
                badgeStyleOverride={theme.pageTheme.marketedSpecialOffer.badge}
                landingPageLink={
                  props.product?.marketedSpecialOffer?.landingPageLink ||
                  undefined
                }
                containerStyleOverride={
                  theme.pageTheme.marketedSpecialOffer.containerStyle
                }
                modalStyleOverride={theme.pageTheme.marketedSpecialOffer.modal}
              />
            )}
          {!showPowerReview && props.product?.reviews?.averageScore ? (
            <div style={{ marginBottom: spacing(1) }}>
              <ProductReview
                starRating={props.product?.reviews?.averageScore}
                reviewLinkUrl={props.product.url.replace('.html', '.reviews')}
                numberOfReviews={props.product?.reviews?.total}
                screenReaderOnlyText={`${props.product?.reviews?.averageScore} ${i18nText.reviewsStarsLabel} ${props.product.reviews?.total} ${i18nText.reviewsLabel}`}
                colorFills={
                  theme.patterns.productBlock.components.productReview
                    .colorFills || undefined
                }
              />
            </div>
          ) : null}
          {'attributes' in props.product && props.product.attributes?.length ? (
            <div>
              {props.product.attributes.map((attribute) => (
                <React.Fragment>
                  <AttributeTitle>{attribute.key}:</AttributeTitle>
                  <span>{attribute.value}</span>
                </React.Fragment>
              ))}
            </div>
          ) : null}
          <OptionsButtonContainer>
            {Options}
            <OutOfStockNotificationRenderer
              selectedVariant={selectedVariantValue as VariantFields}
              mainProduct={props.mainProduct}
              url={props.product.url}
            />
          </OptionsButtonContainer>
        </div>
      </RecommendationItemContainer>
      <MobileOptionButtonContainer>
        {Options}
        <OutOfStockNotificationRenderer
          selectedVariant={selectedVariantValue as VariantFields}
          mainProduct={props.mainProduct}
          url={props.product.url}
        />
      </MobileOptionButtonContainer>
    </div>
  )
}
